/* eslint-disable prettier/prettier */
import SyncIcon from '@mui/icons-material/Sync'
import {
  Badge,
  Box,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useParams } from 'react-router-dom'

import { Page } from '../../components'
import { ChatBox } from '../../components/ChatServices/ChatBox'
import CustomerChatPanelWrapper from '../../components/ChatServices/CustomerChatPanelWrapper'
import AdvancedOptionsMenu from '../../components/Estimates/AdvancedOptionsMenu'
import AuthorizationDialogContainer from '../../components/Estimates/AuthorizationDialog/AuthorizationDialogContainer'
import { ConcernAndNotes } from '../../components/Estimates/ConcernAndNotes/ConcernAndNotes'
import RelatedPanel from '../../components/Estimates/RelatedPanel'
import { ROStatusSelect } from '../../components/Estimates/ROStatusSelect'
import { ServiceTableContainer } from '../../components/Estimates/ServiceTableContainer'
import { OutlinedBox } from '../../components/SharedUI'
import { sdk } from '../../config'
import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NewServiceStatus } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import {
  checkRepairOrderClosedRecently,
  useTriggerCodatFunctionWithNotification
} from '../../hooks/useTriggerCodatFunctionWithNotification/useTriggerCodatFunctionWithNotification'
import { TwilioConversationsContext, WindowSizeContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { RepairOrderContext } from '../../providers/RepairOrderProvider/RepairOrderProvider'
import { useLazyGetServiceItemsQuery } from '../../redux/api'
import { useGenerateRepairOrderCustomerSummaryMutation } from '../../redux/api/independentApi'
import { ServiceInfoStatusEnum, ShopInfo } from '../../sdk'
import { defaultTimeZoneDateToUtcDate } from '../../utils'
import { EstimateCustomerAdvisorInfo } from './EstimateCustomerAdvisorInfo'
import { EstimateDetailsButtons } from './EstimateDetailsButtons'
import { EstimateSidePanel } from './EstimateSidePanel'

export default function EstimateDetails() {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { shopProfile } = useContext(AppContext)
  const {
    isLoading,
    customer,
    transformedServiceItems,
    repairOrder,
    services,
    vehicle
  } = useContext(RepairOrderContext)
  const { conversationUniqueNamesWithUnreadMessages } = useContext(
    TwilioConversationsContext
  )
  const { id } = useParams()

  const [triggerGetServices] = useLazyGetServiceItemsQuery()

  useEffect(() => {
    const bc = new BroadcastChannel('RepairLinkPunchout')
    bc.onmessage = () => {
      // refreshServices
      triggerGetServices({
        authorization: accessToken,
        shop: shopId,
        repairOrder: [id!]
      })
    }
    return () => bc.close()
  }, [accessToken, id, shopId, triggerGetServices])

  const title = useMemo(() => {
    if (!repairOrder) {
      return ''
    }
    const info = repairOrder.entry!.info!
    const isEstimate = info.isEstimate!
    const deleted = !!repairOrder.entry!.info!.archived
    // eslint-disable-next-line prettier/prettier
    return `${isEstimate ? 'Estimate' : 'Repair Order'} #${
      repairOrder.entry!.localId
    } ${deleted ? '(Deleted)' : ''}`
  }, [repairOrder])

  const isEstimate = useMemo(() => {
    return repairOrder ? repairOrder.entry!.info!.isEstimate : true
  }, [repairOrder])

  const containerRef = useRef<HTMLDivElement>(null)
  const [customerChatIsOpen, setCustomerChatIsOpen] = useState(false)

  const { width } = useContext(WindowSizeContext)
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT

  const [statusFilterOption, setStatusFilterOption] = useState<
    NewServiceStatus | 'All'
  >('All')

  const [generateRepairOrderCustomerSummary] =
    useGenerateRepairOrderCustomerSummaryMutation()

  const generateRoSummary = useCallback(async () => {
    if (!repairOrder || !services || !customer || !shopProfile) {
      return ''
    }
    return await generateRepairOrderCustomerSummary({
      authorization: accessToken,
      repairOrderCustomerSummaryRequest: {
        repairOrderExpanded: repairOrder,
        services: services.filter(
          (s) => s.entry!.info!.status !== ServiceInfoStatusEnum.Deferred
        ),
        shopEntry: {
          ...shopProfile,
          info: {
            ...shopProfile.info,
            // sdk added time zone offset to the original time, need to convert it back to utc to avoid backend validation error
            calendarStartTime: defaultTimeZoneDateToUtcDate(
              new Date(shopProfile.info!.calendarStartTime!)
            ),
            calendarEndTime: defaultTimeZoneDateToUtcDate(
              new Date(shopProfile.info!.calendarEndTime!)
            )
          } as ShopInfo
        },
        customerEntry: customer
      }
    }).unwrap()
  }, [
    accessToken,
    customer,
    generateRepairOrderCustomerSummary,
    repairOrder,
    services,
    shopProfile
  ])

  const repairOrderIsClosedAndRecent = useMemo(() => {
    return checkRepairOrderClosedRecently(repairOrder)
  }, [repairOrder])

  const { triggerCodatFunction, loadingTriggerCodat } =
    useTriggerCodatFunctionWithNotification()

  const retrySyncToCodat = useCallback(async () => {
    if (customer?.information?.codatId === 'Failed') {
      await sdk.codatCustomerCreation({
        codatCustomerCreationRequest: {
          customerId: customer.id,
          codatId: undefined,
          codatPushKey: undefined
        }
      })
    }
    if (repairOrder?.entry?.info?.codatId === 'Failed') {
      await sdk.codatInvoiceCreation({
        codatInvoiceCreationRequest: {
          repairOrderId: repairOrder?.entry?.id,
          localId: repairOrder?.entry?.localId,
          codatId: undefined,
          codatPushKey: undefined
        }
      })
    }
    await triggerCodatFunction({
      authorization: accessToken,
      shop: shopId
    })
  }, [
    customer?.id,
    customer?.information?.codatId,
    repairOrder?.entry?.info?.codatId,
    accessToken,
    repairOrder?.entry?.id,
    repairOrder?.entry?.localId,
    shopId,
    triggerCodatFunction
  ])

  return (
    <Page title={title} isLoading={isLoading}>
      {transformedServiceItems && customer && (
        <div ref={containerRef}>
          <OutlinedBox
            title={title}
            noPadding
            spacing={{ sm: 1, md: 0 }}
            direction={{ sm: 'column', md: 'row' }}
            alignItems={{ sm: 'start', md: 'start' }}
            actionsLeft={
              isEstimate ? undefined : (
                <Stack direction="row" spacing={2}>
                  <ChatBox
                    repairOrderId={repairOrder!.entry!.id!}
                    repairOrderNumber={repairOrder!.entry!.localId!}
                    containerRef={containerRef}
                  />
                  {(customer.information?.codatId === 'Failed' ||
                    repairOrder?.entry?.info?.codatId === 'Failed') &&
                    repairOrderIsClosedAndRecent && (
                      <Tooltip title="Retry Quickbooks Invoice Sync">
                        <span>
                          <IconButton
                            onClick={retrySyncToCodat}
                            disabled={loadingTriggerCodat}>
                            <SyncIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                </Stack>
              )
            }
            actionsRight={
              <Stack direction="row" spacing={1} alignItems={'center'}>
                {repairOrder && !isEstimate && (
                  <>
                    <ROStatusSelect repairOrder={repairOrder} />
                  </>
                )}
                <AuthorizationDialogContainer
                  repairOrder={repairOrder!}
                  transformedServiceItems={transformedServiceItems}
                  customer={customer}
                  setStatusFilterOption={setStatusFilterOption}
                  vehicle={vehicle}
                />
                <AdvancedOptionsMenu />
              </Stack>
            }
            actionsBelow={
              <Stack direction="column" spacing={2}>
                <EstimateCustomerAdvisorInfo />
                <EstimateDetailsButtons />
              </Stack>
            }
            disableDivider={true}
          />
          <Grid container spacing={2} data-testid="EstimatesDetailsContent">
            <Grid item xs={12} md={9}>
              {!overTabletBreakpoint && (
                <Box pt={2}>
                  <RelatedPanel
                    repairOrderId={id!}
                    setCustomerChatIsOpen={setCustomerChatIsOpen}
                  />
                </Box>
              )}
              <ConcernAndNotes repairOrder={repairOrder!} />
              <ServiceTableContainer
                statusFilterOption={statusFilterOption}
                setStatusFilterOption={setStatusFilterOption}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {overTabletBreakpoint && (
                <ToggleButtonGroup
                  fullWidth
                  exclusive
                  color="info"
                  size="small"
                  sx={{ paddingTop: 2 }}
                  onChange={(_event, value) => {
                    if (value === 'false') {
                      setCustomerChatIsOpen(false)
                    } else {
                      setCustomerChatIsOpen(true)
                    }
                  }}>
                  <ToggleButton value={'false'} selected={!customerChatIsOpen}>
                    Summary
                  </ToggleButton>
                  <ToggleButton value={'true'} selected={customerChatIsOpen}>
                    <Badge
                      badgeContent=" "
                      color="error"
                      variant="dot"
                      invisible={
                        !conversationUniqueNamesWithUnreadMessages.includes(
                          customer?.id || ''
                        )
                      }>
                      SMS
                    </Badge>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}

              <EstimateSidePanel
                customerChatIsOpen={customerChatIsOpen}
                setCustomerChatIsOpen={setCustomerChatIsOpen}
                repairOrderId={id!}
                customer={customer}
              />
              {customer && (
                <CustomerChatPanelWrapper
                  customerChatIsOpen={customerChatIsOpen}
                  setCustomerChatIsOpen={setCustomerChatIsOpen}
                  customer={customer}
                  generateRoSummary={generateRoSummary}
                />
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </Page>
  )
}
